import type { AxeExplique } from '../apiMatching'
import type { Langue } from '../i18n'

/**
 * L'explication d'un score, par axe.
 *
 * **C'est le composant que L10 attend de ce lot** (conception L10, §6) : barre de répartition et
 * ligne d'explication. L8 le réutilisera pour les avis. Il s'écrit donc une fois, ici, et pas dans
 * chaque écran.
 *
 * **Aucune phrase ne vient du serveur.** L'API rend un `explanationCode` et une charge utile ; la
 * phrase se compose ici, en français ou en arabe. C'est ce qui rend l'arabe de L9 gratuit sur ce
 * module, et ce qui permet de rejouer l'explication d'un score de mars sans disposer du code de
 * mars (modèle §8.2).
 *
 * **Le poids affiché est le poids effectif**, pas le poids nominal : quand la réputation est
 * neutralisée, ses dix points sont répartis sur les autres axes et la somme des pourcentages
 * montrés fait toujours 100. Afficher le nominal donnerait une explication dont les parts ne
 * s'additionnent pas.
 */
export function ExplicationScore({ axes, langue }: { axes: AxeExplique[]; langue: Langue }) {
  const pourcent = new Intl.NumberFormat(langue, { style: 'percent', maximumFractionDigits: 0 })

  return (
    <ul className="explication">
      {axes.map((axe) => (
        <li key={axe.axis} className="explication__axe">
          <p className="explication__entete">
            <span className="explication__nom">{NOM_AXE[langue][axe.axis]}</span>
            <span className="explication__poids">{pourcent.format(axe.weight)}</span>
          </p>

          {/* Barre de repartition : largeur = score BRUT de l'axe, opacite = poids effectif.
              Deux dimensions sur une seule barre, parce qu'un axe fort mal note et un axe faible
              bien note ne se lisent pas pareil. */}
          <p
            className="explication__barre"
            role="img"
            aria-label={`${NOM_AXE[langue][axe.axis]} ${pourcent.format(axe.rawScore)}`}
          >
            <span
              className="explication__jauge"
              style={{ inlineSize: `${Math.round(axe.rawScore * 100)}%` }}
            />
          </p>

          <p className="explication__phrase">{phrase(axe, langue)}</p>
        </li>
      ))}
    </ul>
  )
}

/**
 * Le nom d'un axe, dans la langue lue.
 *
 * Exporté pour le fil de recommandations, qui affiche les axes en puces sans leur barre (ticket
 * `fo-recommandations-01`). Recopier la table là-bas la ferait diverger au premier axe ajouté —
 * c'est exactement ce qui est arrivé à la table des booléens de `Lifestyle`.
 */
export function nomAxe(axis: string, langue: Langue): string {
  return NOM_AXE[langue][axis] ?? axis
}

const NOM_AXE: Record<Langue, Record<string, string>> = {
  fr: {
    Budget: 'Budget',
    Location: 'Trajet',
    Schedule: 'Calendrier',
    Lifestyle: 'Mode de vie',
    Reputation: 'Réputation',
    Features: 'Équipements',
  },
  ar: {
    Budget: 'الميزانية',
    Location: 'المسافة',
    Schedule: 'التواريخ',
    Lifestyle: 'نمط الحياة',
    Reputation: 'السمعة',
    Features: 'التجهيزات',
  },
  en: {
    Budget: 'Budget',
    Location: 'Commute',
    Schedule: 'Dates',
    Lifestyle: 'Lifestyle',
    Reputation: 'Reputation',
    Features: 'Amenities',
  },
}

/**
 * Le code devient une phrase.
 *
 * Un code inconnu tombe sur le nom de l'axe plutôt que sur une chaîne vide : une ligne muette sous
 * un score est pire qu'une ligne imprécise, et c'est exactement ce qui arriverait si le serveur
 * gagnait un code que le front ne connaît pas encore.
 */
function phrase(axe: AxeExplique, langue: Langue): string {
  const d = axe.explanationData as Record<string, string | number | string[] | undefined>
  const nombre = new Intl.NumberFormat(langue)

  const fr: Record<string, string> = {
    BUDGET_WITHIN: 'Dans votre budget',
    BUDGET_STRETCH: `Au-dessus de votre budget (${nombre.format(Number(d.rent ?? 0))} MAD)`,
    BUDGET_OVER: 'Hors budget',
    TRAVEL_TIME_OK: `${nombre.format(Number(d.minutes ?? 0))} min de ${d.anchor ?? ''}`,
    TRAVEL_TIME_FAR: `${nombre.format(Number(d.minutes ?? 0))} min de ${d.anchor ?? ''} — loin`,
    TRAVEL_TIME_ESTIMATED: `~${nombre.format(Number(d.minutes ?? 0))} min de ${d.anchor ?? ''} (estimé)`,
    TRAVEL_NO_ANCHOR: 'Aucun point de repère déclaré',
    SCHEDULE_OVERLAP: 'Disponible dans votre fenêtre',
    SCHEDULE_TIGHT: 'Disponible un peu plus tard',
    DURATION_MISMATCH: `Bail de ${nombre.format(Number(d.minimumStayMonths ?? 0))} mois minimum`,
    LIFESTYLE_ALIGNED: 'Rythmes compatibles',
    LIFESTYLE_FRICTION: 'Quelques différences de mode de vie',
    LIFESTYLE_UNDECLARED: 'Mode de vie non déclaré de part et d’autre',
    REPUTATION_NEUTRAL: 'Pas encore d’avis — cet axe ne compte pas',
    FEATURES_MATCHED: 'Tous vos équipements essentiels',
    FEATURES_MISSING: 'Certains équipements manquent',
    FEATURES_NO_PREFERENCE: 'Aucun équipement exigé',
  }

  const ar: Record<string, string> = {
    BUDGET_WITHIN: 'ضمن ميزانيتك',
    BUDGET_STRETCH: `فوق ميزانيتك (${nombre.format(Number(d.rent ?? 0))} درهم)`,
    BUDGET_OVER: 'خارج الميزانية',
    TRAVEL_TIME_OK: `${nombre.format(Number(d.minutes ?? 0))} دقيقة من ${d.anchor ?? ''}`,
    TRAVEL_TIME_FAR: `${nombre.format(Number(d.minutes ?? 0))} دقيقة من ${d.anchor ?? ''} — بعيد`,
    TRAVEL_TIME_ESTIMATED: `~${nombre.format(Number(d.minutes ?? 0))} دقيقة من ${d.anchor ?? ''} (تقديري)`,
    TRAVEL_NO_ANCHOR: 'لم تحدد أي نقطة مرجعية',
    SCHEDULE_OVERLAP: 'متاح في الفترة التي حددتها',
    SCHEDULE_TIGHT: 'متاح بعد ذلك بقليل',
    DURATION_MISMATCH: `عقد لمدة ${nombre.format(Number(d.minimumStayMonths ?? 0))} أشهر على الأقل`,
    LIFESTYLE_ALIGNED: 'أنماط متوافقة',
    LIFESTYLE_FRICTION: 'بعض الاختلافات في نمط الحياة',
    LIFESTYLE_UNDECLARED: 'نمط الحياة غير مصرّح به من الطرفين',
    REPUTATION_NEUTRAL: 'لا آراء بعد — هذا المحور لا يُحتسب',
    FEATURES_MATCHED: 'كل التجهيزات الأساسية متوفرة',
    FEATURES_MISSING: 'بعض التجهيزات ناقصة',
    FEATURES_NO_PREFERENCE: 'لم تشترط أي تجهيز',
  }

  const en: Record<string, string> = {
    BUDGET_WITHIN: 'Within your budget',
    BUDGET_STRETCH: `Above your budget (${nombre.format(Number(d.rent ?? 0))} MAD)`,
    BUDGET_OVER: 'Out of budget',
    TRAVEL_TIME_OK: `${nombre.format(Number(d.minutes ?? 0))} min from ${d.anchor ?? ''}`,
    TRAVEL_TIME_FAR: `${nombre.format(Number(d.minutes ?? 0))} min from ${d.anchor ?? ''} — far`,
    TRAVEL_TIME_ESTIMATED: `~${nombre.format(Number(d.minutes ?? 0))} min from ${d.anchor ?? ''} (estimated)`,
    TRAVEL_NO_ANCHOR: 'No landmark declared',
    SCHEDULE_OVERLAP: 'Available within your window',
    SCHEDULE_TIGHT: 'Available a little later',
    DURATION_MISMATCH: `Lease of at least ${nombre.format(Number(d.minimumStayMonths ?? 0))} months`,
    LIFESTYLE_ALIGNED: 'Compatible rhythms',
    LIFESTYLE_FRICTION: 'A few lifestyle differences',
    LIFESTYLE_UNDECLARED: 'Neither side declared a lifestyle',
    REPUTATION_NEUTRAL: 'No reviews yet — this axis does not count',
    FEATURES_MATCHED: 'All of your essential amenities',
    FEATURES_MISSING: 'Some amenities are missing',
    FEATURES_NO_PREFERENCE: 'No amenity required',
  }

  const table = { fr, ar, en }[langue]

  return table[axe.explanationCode] ?? NOM_AXE[langue][axe.axis] ?? axe.explanationCode
}

/**
 * Le motif d'une élimination.
 *
 * Un éliminé n'a pas un score faible : il n'a pas de score (spec §7.3). L'écran doit donc dire
 * *pourquoi*, sans afficher de pourcentage — un « 0 % » laisserait croire à un mauvais classement
 * là où il y a un refus net.
 */
export function MotifElimination({ code, langue }: { code: string; langue: Langue }) {
  const fr: Record<string, string> = {
    GenderPolicy: 'La mixité du foyer ne correspond pas à ce que vous avez déclaré.',
    SmokingIndoor: 'Le tabac à l’intérieur est incompatible avec vos réponses.',
    Pets: 'Les animaux sont incompatibles avec vos réponses.',
    BudgetCeiling: 'Le loyer dépasse nettement votre budget.',
    MinimumStay: 'La durée minimale du bail dépasse nettement ce que vous cherchez.',
  }

  const ar: Record<string, string> = {
    GenderPolicy: 'تركيبة السكن لا تطابق ما صرّحت به.',
    SmokingIndoor: 'التدخين بالداخل لا يتوافق مع إجاباتك.',
    Pets: 'الحيوانات الأليفة لا تتوافق مع إجاباتك.',
    BudgetCeiling: 'الكراء يتجاوز ميزانيتك بوضوح.',
    MinimumStay: 'المدة الدنيا للعقد تتجاوز بوضوح ما تبحث عنه.',
  }

  const en: Record<string, string> = {
    GenderPolicy: 'The make-up of the household does not match what you declared.',
    SmokingIndoor: 'Smoking indoors is incompatible with your answers.',
    Pets: 'Pets are incompatible with your answers.',
    BudgetCeiling: 'The rent is clearly above your budget.',
    MinimumStay: 'The minimum lease term is clearly longer than what you are looking for.',
  }

  const table = { fr, ar, en }[langue]

  return <p className="explication__elimination">{table[code] ?? code}</p>
}
